'use client'

import { useState } from 'react'
import {
  BarChart3,
  Network,
  ThumbsUp,
  ShieldCheck,
  Clock,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  ChevronDown,
  ChevronUp,
  Mail,
  Check,
  Loader2,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import OverallScore from './OverallScore'
import ScoreCard from './ScoreCard'
import SubredditCard from './SubredditCard'
import MentionCard from './MentionCard'
import SeedingMatrix from './SeedingMatrix'
import type { Finding } from './ScoreCard'
import PlatformCTA from '@/components/platform/PlatformCTA'

// --- Result type matching the API response ---

interface SeedingSignal {
  signal: string
  points: number
}

interface MentionResult {
  id: string
  title: string
  body: string
  author: string
  subreddit: string
  score: number
  numComments: number
  createdUtc: number
  permalink: string
  seedingRisk: number
  riskLevel: 'low' | 'medium' | 'high'
  seedingSignals: SeedingSignal[]
  gptRefined: boolean
  writingFormality?: string
  sentiment: 'positive' | 'neutral' | 'negative'
  accountAgeDays: number | null
  totalKarma: number | null
  brandDensity: number
}

interface SubredditBreakdown {
  subreddit: string
  mentionCount: number
  avgSentiment: string
  avgSeedingRisk: number
  topPost: { title: string; permalink: string } | null
}

interface RedditMonitorResult {
  brand: string
  subredditsSearched: string[]
  keywords: string[]
  checkedAt: string
  totalMentions: number
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  mentions: MentionResult[]
  subredditBreakdown: SubredditBreakdown[]
  scoreBreakdown: {
    category: string
    score: number
    maxScore: number
    details: string
  }[]
  seedingMatrix: {
    organic: number
    suspicious: number
    likelySeeded: number
  }
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
  skippedSubreddits: string[]
  /**
   * Entity-verification fields. OPTIONAL because results saved before
   * verification shipped do not carry them, and those reports must keep
   * rendering exactly as they did. Absent => no notice, unchanged surface.
   */
  ambiguousMatchCount?: number
  entityVerification?: {
    applied: boolean
    verifiedCount: number
    withheldCount: number
  }
}

interface RedditResultsProps {
  result: RedditMonitorResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (the email-share row, `PlatformCTA`, and the gradient
   * services CTA) so the SAME rich report can be reused inside `/platform/app`.
   * Result content (score ring, AI analysis, score breakdown with `findingsFor`,
   * subreddit breakdown, seeding matrix, mentions list with show-10/show-all,
   * recommendations with blog links) is identical either way. Locked/teaser
   * redaction is data-driven and handled by the page (which renders a teaser
   * surface instead of this component when not unlocked) — this prop does not
   * affect it.
   */
  variant?: 'public' | 'dashboard'
}

const BLOG_LINKS: Record<string, string> = {
  'Mention Volume': '/blogs/build-brand-ai-search-engines-cite',
  'Organic vs Seeded':
    '/blogs/geo-playbook-get-cited-chatgpt-perplexity-claude',
  Sentiment: '/blogs/optimized-ai-search-pixelmojo-results',
}

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

const SCORE_ICONS: Record<string, React.ReactNode> = {
  'Mention Volume': <BarChart3 size={18} />,
  'Subreddit Diversity': <Network size={18} />,
  Sentiment: <ThumbsUp size={18} />,
  'Organic vs Seeded': <ShieldCheck size={18} />,
  Recency: <Clock size={18} />,
}

const SCORE_COLORS: Record<string, string> = {
  'Mention Volume': '#008dcc',
  'Subreddit Diversity': '#8b5cf6',
  Sentiment: '#22c55e',
  'Organic vs Seeded': '#F90B8A',
  Recency: '#FDC304',
}

export default function RedditResults({
  result,
  variant = 'public',
}: RedditResultsProps) {
  const [showAllMentions, setShowAllMentions] = useState(false)
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [shareError, setShareError] = useState('')

  async function handleEmailShare() {
    if (
      !shareEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
    ) {
      setShareError('Please enter a valid email address.')
      setShareStatus('error')
      return
    }

    setShareStatus('sending')
    setShareError('')

    try {
      const res = await fetch('/api/tools/reddit-monitor/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          brand: result.brand,
          overallScore: result.overallScore,
          grade: result.grade,
          totalMentions: result.totalMentions,
          seedingMatrix: result.seedingMatrix,
          scoreBreakdown: result.scoreBreakdown,
          subredditBreakdown: result.subredditBreakdown,
          recommendations: result.recommendations,
          aiSummary: result.aiSummary?.summary || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to send email.')
      }

      setShareStatus('sent')
      setTimeout(() => {
        setShareStatus('idle')
        setShowEmailInput(false)
        setShareEmail('')
      }, 2000)
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : 'Failed to send email.'
      )
      setShareStatus('error')
    }
  }

  const visibleMentions = showAllMentions
    ? result.mentions
    : result.mentions.slice(0, 10)

  // Gate on `typeof`, not truthiness: a historical result has no verification
  // fields at all and must render unchanged, while a verified run that
  // withheld nothing is a real 0 and not the same thing as "absent".
  const verificationApplied = result.entityVerification?.applied === true
  const withheldCount =
    typeof result.ambiguousMatchCount === 'number'
      ? result.ambiguousMatchCount
      : (result.entityVerification?.withheldCount ?? 0)

  function findingsFor(category: string): Finding[] {
    const breakdown = result.scoreBreakdown.find(s => s.category === category)
    if (!breakdown) return []
    const findings: Finding[] = []
    const pct =
      breakdown.maxScore > 0
        ? Math.round((breakdown.score / breakdown.maxScore) * 100)
        : 0

    switch (category) {
      case 'Mention Volume': {
        findings.push({
          text: `${result.totalMentions} total mention${result.totalMentions !== 1 ? 's' : ''} found`,
          status:
            result.totalMentions >= 5
              ? 'good'
              : result.totalMentions > 0
                ? 'neutral'
                : 'bad',
        })
        if (result.subredditsSearched.length > 0) {
          findings.push({
            text: `Searched ${result.subredditsSearched.join(', ')}`,
            status: 'neutral',
          })
        }
        break
      }
      case 'Subreddit Diversity': {
        const uniqueSubs = result.subredditBreakdown.length
        findings.push({
          text: `Brand appears in ${uniqueSubs} unique subreddit${uniqueSubs !== 1 ? 's' : ''}`,
          status:
            uniqueSubs >= 3 ? 'good' : uniqueSubs >= 2 ? 'neutral' : 'bad',
        })
        for (const sub of result.subredditBreakdown.slice(0, 3)) {
          findings.push({
            text: `r/${sub.subreddit}: ${sub.mentionCount} mention${sub.mentionCount !== 1 ? 's' : ''}`,
            status: 'neutral',
          })
        }
        break
      }
      case 'Sentiment': {
        const pos = result.mentions.filter(
          m => m.sentiment === 'positive'
        ).length
        const neg = result.mentions.filter(
          m => m.sentiment === 'negative'
        ).length
        const neu = result.mentions.filter(
          m => m.sentiment === 'neutral'
        ).length
        if (pos > 0)
          findings.push({
            text: `${pos} positive mention${pos !== 1 ? 's' : ''}`,
            status: 'good',
          })
        if (neu > 0)
          findings.push({
            text: `${neu} neutral mention${neu !== 1 ? 's' : ''}`,
            status: 'neutral',
          })
        if (neg > 0)
          findings.push({
            text: `${neg} negative mention${neg !== 1 ? 's' : ''}`,
            status: 'bad',
          })
        if (result.totalMentions === 0)
          findings.push({ text: 'No mentions to analyze', status: 'bad' })
        break
      }
      case 'Organic vs Seeded': {
        findings.push({
          text: `${result.seedingMatrix.organic} organic, ${result.seedingMatrix.suspicious} suspicious, ${result.seedingMatrix.likelySeeded} likely seeded`,
          status: pct >= 70 ? 'good' : pct >= 40 ? 'neutral' : 'bad',
        })
        if (result.seedingMatrix.likelySeeded > 0) {
          findings.push({
            text: `${result.seedingMatrix.likelySeeded} post${result.seedingMatrix.likelySeeded !== 1 ? 's' : ''} show strong seeding indicators`,
            status: 'bad',
          })
        }
        break
      }
      case 'Recency': {
        const now = Date.now() / 1000
        const recent = result.mentions.filter(
          m => m.createdUtc > now - 30 * 86400
        ).length
        findings.push({
          text: `${recent} mention${recent !== 1 ? 's' : ''} in the last 30 days`,
          status: recent >= 3 ? 'good' : recent > 0 ? 'neutral' : 'bad',
        })
        break
      }
    }

    return findings
  }

  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>
              Reddit monitoring results for
            </p>
            <p className='text-sm font-medium text-foreground'>
              {result.brand}
            </p>
            <p className='text-xs text-muted-foreground'>
              {result.totalMentions}{' '}
              {verificationApplied ? 'verified mention' : 'mention'}
              {result.totalMentions !== 1 ? 's' : ''} found
              {result.subredditsSearched.length > 0 &&
                result.subredditsSearched[0] !== 'all' && (
                  <>
                    {' '}
                    in {result.subredditsSearched.map(s => `r/${s}`).join(', ')}
                  </>
                )}
              {result.keywords.length > 0 && (
                <> &middot; Keywords: {result.keywords.join(', ')}</>
              )}
            </p>
          </div>
          <OverallScore score={result.overallScore} grade={result.grade} />
          {withheldCount > 0 && (
            <p className='mt-4 flex items-start gap-2 border-t border-border/60 pt-4 text-xs text-muted-foreground'>
              <Info size={14} className='mt-px shrink-0 text-primary' />
              <span>
                {withheldCount} same-name{' '}
                {withheldCount === 1 ? 'result was' : 'results were'} withheld:{' '}
                {withheldCount === 1 ? 'it mentions' : 'they mention'} &ldquo;
                {result.brand}&rdquo; but{' '}
                {withheldCount === 1 ? 'carries' : 'carry'} no evidence of your
                brand, so {withheldCount === 1 ? 'it is' : 'they are'} excluded
                from the mention count and score.
              </span>
            </p>
          )}
        </div>
      </LenisFadeIn>

      {/* Email share — public surface only */}
      {variant !== 'dashboard' && (
        <div className='flex flex-col items-center gap-2'>
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              className='inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors'
            >
              <Mail size={16} />
              Email results
            </button>
          ) : (
            <div className='flex items-center gap-2 w-full max-w-md'>
              <input
                type='email'
                value={shareEmail}
                onChange={e => {
                  setShareEmail(e.target.value)
                  if (shareStatus === 'error') setShareStatus('idle')
                }}
                placeholder='colleague@company.com'
                className='flex-1 h-10 px-4 text-sm rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40'
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailShare()
                }}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                autoFocus
              />
              <button
                onClick={handleEmailShare}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                className='inline-flex items-center gap-2 text-sm font-medium h-10 px-5 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors disabled:opacity-50'
              >
                {shareStatus === 'sending' ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : shareStatus === 'sent' ? (
                  <>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e' }}>Sent!</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
          {shareStatus === 'error' && shareError && (
            <p className='text-xs' style={{ color: '#F90B8A' }}>
              {shareError}
            </p>
          )}
        </div>
      )}

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='Reddit'
          score={result.overallScore}
          grade={result.grade}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Score Breakdown */}
      <LenisStaggered>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {result.scoreBreakdown.map(s => (
            <ScoreCard
              key={s.category}
              title={s.category}
              icon={SCORE_ICONS[s.category] || <BarChart3 size={18} />}
              score={s.score}
              maxScore={s.maxScore}
              findings={findingsFor(s.category)}
              color={SCORE_COLORS[s.category] || '#008dcc'}
            />
          ))}
        </div>
      </LenisStaggered>

      {/* Subreddit Breakdown */}
      {result.subredditBreakdown.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Subreddit Breakdown
            </h2>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              {result.subredditBreakdown.map(sub => (
                <SubredditCard
                  key={sub.subreddit}
                  subreddit={sub.subreddit}
                  mentionCount={sub.mentionCount}
                  avgSentiment={sub.avgSentiment}
                  avgSeedingRisk={sub.avgSeedingRisk}
                  topPost={sub.topPost}
                />
              ))}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Seeding Matrix */}
      <LenisFadeIn>
        <SeedingMatrix
          organic={result.seedingMatrix.organic}
          suspicious={result.seedingMatrix.suspicious}
          likelySeeded={result.seedingMatrix.likelySeeded}
        />
      </LenisFadeIn>

      {/* Mentions */}
      {result.mentions.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-lg font-semibold text-foreground'>
                Mentions ({result.mentions.length})
              </h2>
              {result.mentions.length > 10 && (
                <button
                  onClick={() => setShowAllMentions(!showAllMentions)}
                  className='text-xs text-primary hover:underline flex items-center gap-1'
                >
                  {showAllMentions ? (
                    <>
                      Show less <ChevronUp size={14} />
                    </>
                  ) : (
                    <>
                      Show all {result.mentions.length}{' '}
                      <ChevronDown size={14} />
                    </>
                  )}
                </button>
              )}
            </div>
            <div className='space-y-3'>
              {visibleMentions.map(mention => (
                <MentionCard
                  key={mention.id}
                  title={mention.title}
                  body={mention.body}
                  author={mention.author}
                  subreddit={mention.subreddit}
                  score={mention.score}
                  numComments={mention.numComments}
                  createdUtc={mention.createdUtc}
                  permalink={mention.permalink}
                  seedingRisk={mention.seedingRisk}
                  riskLevel={mention.riskLevel}
                  seedingSignals={mention.seedingSignals}
                  gptRefined={mention.gptRefined}
                  sentiment={mention.sentiment}
                  accountAgeDays={mention.accountAgeDays}
                  totalKarma={mention.totalKarma}
                />
              ))}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* No mentions state */}
      {result.mentions.length === 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-8 text-center'>
            <p className='text-sm text-foreground/80 mb-2'>
              No Reddit mentions found for &quot;{result.brand}&quot;
            </p>
            <p className='text-xs text-muted-foreground'>
              Try broadening your search by removing subreddit filters or adding
              related keywords.
            </p>
          </div>
        </LenisFadeIn>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Recommendations
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                const blogLink = BLOG_LINKS[rec.category]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                      {blogLink && (
                        <a
                          href={blogLink}
                          className='inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5'
                        >
                          Learn more <ArrowRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #8c1b4c, #b32a6c, #e54b8c)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Want to protect your brand on Reddit?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              Our AI visibility audit includes Reddit monitoring, seeding
              detection, and a strategy to build authentic community presence.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#b32a6c'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Strategy Session
              </a>
              <a
                href='/platform'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Or DIY with Radar (free){' '}
                <ArrowRight size={16} className='ml-1' />
              </a>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
